import { captureException, startSpan } from "@sentry/react";

import {
    ILoraResponse,
    IModelDataResponse,
    IPresignedUrlsResponse,
    LoraDTO,
} from "../../types";
import { doDelete, doGet, doGetOptAuth, doPostJson } from "../core/http";

export const getModelConfig = async (): Promise<IModelDataResponse> => {
    try {
        const url = "/api/v1/models/configurations";
        const { data, error } = await startSpan(
            { name: "getModelConfigs" },
            () => {
                return doGet(url, false);
            }
        );

        if (error) {
            captureException(error);
            return {
                modelConfigs: [],
                samplers: [],
                models: [],
            };
        }

        return data;
    } catch (e) {
        captureException(e);
        return {
            modelConfigs: [],
            samplers: [],
            models: [],
        };
    }
};

export const getInpaintingModelConfig =
    async (): Promise<IModelDataResponse> => {
        try {
            const url = "/api/v1/models/inpainting/configurations";
            const { data, error } = await startSpan(
                { name: "getInpaintingModelConfigs" },
                () => {
                    return doGet(url, false);
                }
            );

            if (error) {
                captureException(error);
                return {
                    modelConfigs: [],
                    samplers: [],
                    models: [],
                };
            }

            return data;
        } catch (e) {
            captureException(e);
            return {
                modelConfigs: [],
                samplers: [],
                models: [],
            };
        }
    };

export const getVideoModelConfig = async (): Promise<IModelDataResponse> => {
    try {
        const url = "/api/v1/models/video/configurations";
        const { data, error } = await startSpan(
            { name: "getVideoModelConfigs" }, // Updated span name
            () => {
                return doGet(url, false);
            }
        );

        if (error) {
            captureException(error);
            return {
                modelConfigs: [],
                samplers: [],
                models: [],
            };
        }

        return data;
    } catch (e) {
        captureException(e);
        return {
            modelConfigs: [],
            samplers: [],
            models: [],
        };
    }
};

export const getUpscalerModelConfig = async (): Promise<IModelDataResponse> => {
    try {
        const url = "/api/v1/models/upscaler/configurations";
        const { data, error } = await startSpan(
            { name: "getUpscalerModelConfigs" },
            () => {
                return doGet(url, false);
            }
        );

        if (error) {
            captureException(error);
            return {
                modelConfigs: [],
                samplers: [],
                models: [],
            };
        }

        return data;
    } catch (e) {
        captureException(e);
        return {
            modelConfigs: [],
            samplers: [],
            models: [],
        };
    }
};

export const getAudioModelConfig = async (): Promise<IModelDataResponse> => {
    try {
        const url = "/api/v1/models/audio/configurations";
        const { data, error } = await startSpan(
            { name: "getAudioModelConfigs" }, // Updated span name
            () => {
                return doGet(url, false);
            }
        );

        if (error) {
            captureException(error);
            return {
                modelConfigs: [],
                samplers: [],
                models: [],
            };
        }

        return data;
    } catch (e) {
        captureException(e);
        return {
            modelConfigs: [],
            samplers: [],
            models: [],
        };
    }
};

// Lora Actions
export const fetchPresignedUrls = async (
    filenames: string[]
): Promise<IPresignedUrlsResponse> => {
    try {
        const { data, error } = await startSpan(
            { name: "fetchPresignedUrls" },
            () => doPostJson("/lora/presigned-urls", true, filenames)
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { urls: data.urls };
    } catch (e: any) {
        captureException(e);
        return { error: e as string };
    }
};

export const createLora = async (payload: Object): Promise<ILoraResponse> => {
    const url = "/lora/create";

    try {
        const { data, error } = await startSpan({ name: "createLora" }, () => {
            return doPostJson(url, true, payload);
        });

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { lora: data };
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: e as string };
    }
};

export const updateLora = async (lora: LoraDTO): Promise<ILoraResponse> => {
    const url = "/lora/update";

    try {
        const { data, error } = await startSpan({ name: "updateLora" }, () => {
            return doPostJson(url, true, lora);
        });

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { lora: data };
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: e as string };
    }
};

export const deleteLora = async (
    loraId: string
): Promise<{ success?: boolean; error?: string }> => {
    const url = `/lora/${loraId}`;

    try {
        const { error } = await startSpan({ name: "deleteLora" }, () => {
            return doDelete(url, true);
        });

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { success: true };
    } catch (e) {
        captureException(e);
        return { error: e as string };
    }
};

export const likeLora = async (
    loraId: string
): Promise<{ success?: boolean; error?: string }> => {
    try {
        const response = await startSpan({ name: "likeLora" }, () => {
            return doPostJson(`/lora/${loraId}/like`, true, {});
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return { success: true };
    } catch (e: any) {
        captureException(e);
        return { error: "Failed to like lora" };
    }
};

export const fetchLora = async (loraId: string): Promise<ILoraResponse> => {
    try {
        const { data, error } = await startSpan(
            { name: "fetchLora" },
            async () => {
                return await doGetOptAuth(`/lora/${loraId}`);
            }
        );

        if (error) {
            captureException(error);
            return { error: error.message };
        }

        return { lora: data };
    } catch (e: any) {
        captureException(e);
        return { error: e as string };
    }
};

export const fetchAllLoras = async (): Promise<{
    loras: LoraDTO[];
    error?: string;
}> => {
    try {
        const response = await startSpan(
            { name: "fetchAllLoras" },
            async () => {
                return await doGet("/lora/all", true);
            }
        );

        if (response.error) {
            captureException(response.error);
            return { loras: [], error: response.error };
        }

        return {
            loras: response.data as LoraDTO[],
        };
    } catch (e: any) {
        captureException(e);
        return { loras: [], error: "Failed to fetch loras" };
    }
};

export const fetchAllPublicLoras = async (): Promise<{
    loras: LoraDTO[];
    error?: string;
}> => {
    try {
        const response = await startSpan(
            { name: "fetchAllPublicLoras" }, // Corrected span name
            async () => {
                return await doGetOptAuth("/lora/public");
            }
        );

        if (response.error) {
            captureException(response.error);
            return { loras: [], error: response.error };
        }

        return {
            loras: response.data as LoraDTO[],
        };
    } catch (e: any) {
        captureException(e);
        return { loras: [], error: "Failed to fetch public loras" };
    }
};
